import React, { useState, useEffect } from 'react';
import { RefreshCw, Download, AlertTriangle, Clock, CheckCircle2, Filter, Layers } from 'lucide-react';
import { riskService } from '../riskService';
import { useTableSort, SortableTh } from '../../../../shared/useTableSort';

export const CentralRiskOverviewBoardTab: React.FC = () => {
  const [items, setItems] = useState<any[]>([]);
  const [lastUpdated, setLastUpdated] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [statusFilter, setStatusFilter] = useState<'ALL' | 'past_due' | 'due_soon' | 'on_track'>('ALL');
  const [sourceTypeFilter, setSourceTypeFilter] = useState<string>('ALL');
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    loadBoard();
  }, [statusFilter]);

  const loadBoard = async () => {
    setLoading(true);
    try {
      const params: Record<string, any> = {};
      if (statusFilter !== 'ALL') params.due_date_category = statusFilter;
      const res = await riskService.getRiskOverviewBoard(params);
      setItems(res?.items || []);
      setLastUpdated(res?.last_updated_utc || '');
    } catch (err: any) {
      console.error('Failed to load risk overview board:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await riskService.syncRiskOverviewBoard();
      setMsg(`Synced ${res?.synced_items || items.length} active risk items across all modules`);
      setTimeout(() => setMsg(null), 4000);
      await loadBoard();
    } catch (err: any) {
      setMsg(`Sync failed: ${err.message}`);
    } finally {
      setSyncing(false);
    }
  };

  const handleExportEasa = async () => {
    try {
      const res = await fetch('/api/v1/risks/plus/registers/overview/export?organizationId=ORG-XYZ');
      const data = await res.json();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `EASA_GM3_Risk_Register_7Tab_${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      setMsg('EASA GM3 7-Tab Risk Register exported successfully');
      setTimeout(() => setMsg(null), 4000);
    } catch (err: any) {
      alert('Export failed: ' + err.message);
    }
  };

  const filteredItems = items.filter((it) => {
    if (sourceTypeFilter !== 'ALL' && it.source_module !== sourceTypeFilter) return false;
    return true;
  });

  const { sortedItems, sortField, sortDirection, toggleSort } = useTableSort(filteredItems);

  return (
    <div className="space-y-4">
      {/* Top action bar */}
      <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex flex-wrap justify-between items-center gap-4">
        <div>
          <h3 className="text-base font-bold text-slate-800 flex items-center gap-2">
            <Layers size={18} className="text-indigo-600" />
            Central Risk Overview Board & Cross-Module Consolidation
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Aggregates safety risks, findings, occurrences and investigations into a unified SLA register
            {lastUpdated && ` • Last synced: ${new Date(lastUpdated).toLocaleTimeString()}`}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={handleSync}
            disabled={syncing}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-indigo-50 text-indigo-700 hover:bg-indigo-100 transition-colors border border-indigo-200"
          >
            <RefreshCw size={14} className={syncing ? 'animate-spin' : ''} />
            {syncing ? 'Syncing...' : 'Sync All Modules'}
          </button>
          <button
            onClick={handleExportEasa}
            className="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-semibold rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 transition-colors shadow-sm"
          >
            <Download size={14} />
            Export EASA GM3 (7-Tab)
          </button>
        </div>
      </div>

      {msg && (
        <div className="p-3 bg-emerald-50 text-emerald-800 text-xs font-semibold rounded-lg border border-emerald-200 flex items-center justify-between">
          <span>✓ {msg}</span>
          <button onClick={() => setMsg(null)} className="text-emerald-600 hover:text-emerald-900">✕</button>
        </div>
      )}

      {/* Filter pills */}
      <div className="flex flex-wrap items-center gap-2 pt-1">
        <div className="flex bg-slate-100 p-1 rounded-lg text-xs font-semibold">
          <button
            onClick={() => setStatusFilter('ALL')}
            className={`px-3 py-1 rounded-md transition-all ${statusFilter === 'ALL' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600'}`}
          >
            All Items ({items.length})
          </button>
          <button
            onClick={() => setStatusFilter('past_due')}
            className={`px-3 py-1 rounded-md transition-all flex items-center gap-1.5 ${statusFilter === 'past_due' ? 'bg-rose-50 text-rose-700 shadow-sm font-bold' : 'text-slate-600'}`}
          >
            <AlertTriangle size={12} className="text-rose-500" />
            Past Due
          </button>
          <button
            onClick={() => setStatusFilter('due_soon')}
            className={`px-3 py-1 rounded-md transition-all flex items-center gap-1.5 ${statusFilter === 'due_soon' ? 'bg-amber-50 text-amber-700 shadow-sm font-bold' : 'text-slate-600'}`}
          >
            <Clock size={12} className="text-amber-500" />
            Due Soon (30d)
          </button>
          <button
            onClick={() => setStatusFilter('on_track')}
            className={`px-3 py-1 rounded-md transition-all flex items-center gap-1.5 ${statusFilter === 'on_track' ? 'bg-emerald-50 text-emerald-700 shadow-sm font-bold' : 'text-slate-600'}`}
          >
            <CheckCircle2 size={12} className="text-emerald-500" />
            On Track
          </button>
        </div>

        <select
          value={sourceTypeFilter}
          onChange={(e) => setSourceTypeFilter(e.target.value)}
          className="text-xs border border-slate-300 rounded-lg px-2.5 py-1.5 bg-white text-slate-700 focus:outline-none focus:ring-1 focus:ring-indigo-500"
        >
          <option value="ALL">All Source Modules</option>
          <option value="SAFETY_RISK">Safety Risk Register</option>
          <option value="INVESTIGATION">Investigations</option>
          <option value="QUALITY_AUDIT">Quality Findings & Audits</option>
          <option value="REPORTING">Occurrence Reporting</option>
        </select>
      </div>

      {/* Main Table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <table className="w-full text-left text-xs">
          <thead className="bg-slate-50 border-b border-slate-200 text-slate-500 uppercase font-semibold">
            <tr>
              <SortableTh field="source_record_id" currentField={sortField} currentDirection={sortDirection} onSort={toggleSort} className="py-3 px-4">
                Source & ID
              </SortableTh>
              <SortableTh field="title" currentField={sortField} currentDirection={sortDirection} onSort={toggleSort} className="py-3 px-4">
                Title / Risk Description
              </SortableTh>
              <SortableTh field="risk_score" currentField={sortField} currentDirection={sortDirection} onSort={toggleSort} className="py-3 px-4">
                Risk Rating
              </SortableTh>
              <SortableTh field="responsible_department" currentField={sortField} currentDirection={sortDirection} onSort={toggleSort} className="py-3 px-4">
                Responsible / Owner
              </SortableTh>
              <SortableTh field="due_date" currentField={sortField} currentDirection={sortDirection} onSort={toggleSort} className="py-3 px-4">
                Due Date
              </SortableTh>
              <SortableTh field="sla_status" currentField={sortField} currentDirection={sortDirection} onSort={toggleSort} className="py-3 px-4">
                SLA Status
              </SortableTh>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr>
                <td colSpan={6} className="text-center py-8 text-slate-400">Loading consolidated risk items...</td>
              </tr>
            ) : sortedItems.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-center py-8 text-slate-400">No consolidated risk items matching filter criteria.</td>
              </tr>
            ) : (
              sortedItems.map((item, idx) => (
                <tr key={idx} className="hover:bg-slate-50">
                  <td className="py-3 px-4 font-mono font-semibold text-indigo-700">
                    <span className="block text-[11px] text-slate-400 uppercase font-sans font-medium">{item.source_module || 'RISK'}</span>
                    {item.source_record_id || `ITEM-${idx + 1}`}
                  </td>
                  <td className="py-3 px-4 font-medium text-slate-800 max-w-md">
                    {item.title || item.risk_description || 'Unspecified risk element'}
                  </td>
                  <td className="py-3 px-4">
                    <span className={`px-2 py-0.5 rounded text-[11px] font-bold ${
                      (item.initial_risk_score >= 15 || item.risk_level === 'HIGH')
                        ? 'bg-rose-100 text-rose-800'
                        : (item.initial_risk_score >= 8 || item.risk_level === 'MEDIUM')
                        ? 'bg-amber-100 text-amber-800'
                        : 'bg-emerald-100 text-emerald-800'
                    }`}>
                      {item.risk_rating || item.risk_level || 'ALARP'}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-slate-600">
                    {item.responsible_user || item.owner || 'Safety Department'}
                  </td>
                  <td className="py-3 px-4 text-slate-600 font-mono">
                    {item.due_date ? String(item.due_date).slice(0, 10) : '—'}
                  </td>
                  <td className="py-3 px-4">
                    {item.due_date_category === 'past_due' ? (
                      <span className="inline-flex items-center gap-1 text-rose-600 font-bold">
                        <AlertTriangle size={12} /> Past Due
                      </span>
                    ) : item.due_date_category === 'due_soon' ? (
                      <span className="inline-flex items-center gap-1 text-amber-600 font-bold">
                        <Clock size={12} /> Due Soon
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-emerald-600 font-medium">
                        <CheckCircle2 size={12} /> On Track
                      </span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
